/**
 * app分析相关
 */
import {
  IS_SNAPSHOT,
  addOnUnloadCallback,
  domContentLoadedPromise,
  getSourceApp,
  getPlatform,
  isWiki,
  getCookie
} from './utils';
import { UserInfo } from './types';
import { isDocsOrEtaProd, isEta } from './config';
import { extendMeta, uuid, getDocId } from './metaData';
import type { EventType } from '@ks/weblogger/lib/types/types/event';

const supportsPushState = (() => {
  const ua = window.navigator.userAgent;
  if (
    (ua.indexOf('Android 2.') !== -1 || ua.indexOf('Android 4.0') !== -1) &&
    ua.indexOf('Mobile Safari') !== -1 &&
    ua.indexOf('Chrome') === -1 &&
    ua.indexOf('Windows Phone') === -1
  ) {
    return false;
  }
  return window.history && 'pushState' in window.history;
})();

let beginTime = new Date().valueOf();

async function loadWebLogger(): Promise<boolean> {
  await domContentLoadedPromise;
  // 截图模式或外部自行引入了EFWebLogger不需要依赖docLogger加载EFWebLogger
  const needLoadInternal = !IS_SNAPSHOT && !window.EFWebLogger;

  // 外部可自行引入EFWebLoggerConfig 方便开启自定义雷达配置
  const needEFWebLoggerConfig = !window.EFWebLoggerConfig;

  //外部自行引入了EFWebLogger的情况下不加载doc配置
  if (needLoadInternal) {
    //docs-app分析配置
    // https://component.corp.kuaishou.com/docs/weblogger/document/instance.html#%E5%AE%9E%E4%BE%8B%E5%8C%96-weblogger
    if (needEFWebLoggerConfig) {
      window.EFWebLoggerConfig = {
        // routes: {
        //   EF_DOCS: [
        //     // https://docs.corp.kuaishou.com/s/home/fcABi_zY5PJtOTpGI5X-vHydk
        //     [
        //       '/d/home/:docId;WORD_PC',
        //       '/s/home/:docId;SHEETS_PC',
        //       '/p/home/:docId',
        //       '/s/mobile/:docId;SHEETS_PC',
        //       '/s/home/htmldata/:docId;SHEETS_PC',
        //       '/permission-apply;HOMEPAGE_PC',
        //       '/no-permission;HOMEPAGE_PC',
        //       '/web/n/pdf-page/:any;HOMEPAGE_PC',
        //       '/web/n/:any;HOMEPAGE_PC',
        //       '/web/:any;HOMEPAGE_PC',
        //       '/pdf-page/:any',
        //       '/e/ydisk/v/home/:any;HOMEPAGE_PC',
        //       '/home;HOMEPAGE_PC',
        //       '/my-space;HOMEPAGE_PC',
        //       '/shared-space;HOMEPAGE_PC',
        //       '/collect;HOMEPAGE_PC',
        //       '/recycle;HOMEPAGE_PC',
        //       '/design-assets;HOMEPAGE_PC',
        //       '/f/:docId;HOMEPAGE_PC',
        //       '/file/:docId;HOMEPAGE_PC',
        //       '/space;HOMEPAGE_PC',
        //       '/export-pending/:docId',
        //       '/pending',
        //       '/modal.html',
        //       '/result.html',
        //       '/wiki/home/:docId;HOMEPAGE_PC',
        //       '/wiki;HOMEPAGE_PC',
        //       '/p/start/:docId',
        //       '/f/mine;HOMEPAGE_PC',
        //       '/empty;HOMEPAGE_PC',
        //       '/k/home;HOMEPAGE_PC',
        //       '/t/home;HOMEPAGE_PC',
        //       '/;HOMEPAGE_PC'
        //     ],
        //     {
        //       docId: '.*',
        //       any: '.*'
        //     }
        //   ]
        // },
        autoPV: false,
        timing: false,
        fps: false,
        crash: false,
        env: isDocsOrEtaProd() ? 'production' : 'test',
        radar: {
          // https://radar.corp.kuaishou.com/project/40273801c0/dashboard
          projectId: '40273801c0', // 兜底项目id
          sampling: 1
        }
      };
    }

    const { Weblog, Radar } = await import(
      /* webpackChunkName: "weblogger_radar" */ './loadWebLogger'
    );

    window.EFWebLogger = new Weblog(
      {
        env: isDocsOrEtaProd() ? 'production' : 'test',
        ...window.EFWebLoggerConfig
      },
      {
        product_name: 'EF_DOCS'
      }
    );

    // https://halo.corp.kuaishou.com/help/docs/0cc3893b847f2cbe09c66791a594831d
    const radar = new Radar({
      weblog: window.EFWebLogger,
      resourceSampling: 1,
      // @ts-ignore radar配置
      ...window.EFWebLoggerConfig.radar
      // 其他参数见下方表格
    });

    // 兼容项目中的旧Radar引用方式
    window.EFWebLogger.plugins.radar = radar;

    const oldPushState = history.pushState;
    const oldReplaceState = history.replaceState;

    // let oldPathName = location.pathname;
    const urlChange = () => {
      // 只有pathname变更才算离开，fix:锚点变更现在会上报一次
      // IMP: 临时方案，基本保证使用时长合理性，锚点变更上报一次进入和离开
      // 现在的逻辑：
      // 1.当天仅有进入，则用24:00减去进入时间，最长计30分钟
      // 2.当天有进入离开，则用离开时间减去进入时间
      // TODO:2022.04.20确定最新用户使用时长口径方案：10分钟内用户有操作（移动，滚动，显示隐藏等等等）则上报一次心跳事件，按照心跳事件次数*10分钟计使用时长
      // if (location.pathname !== oldPathName) {
      // oldPathName = location.pathname;
      setTimeout(() => {
        collect('PV', {
          // eslint-disable-line
          type: 'leave',
          beginTime: beginTime,
          auto: true
        });
        beginTime = new Date().valueOf();
        collect('PV', {
          // eslint-disable-line
          type: 'enter',
          auto: true
        });
      });
      // }
    };

    // 因为有这样的逻辑存在，需要保证docLogger在业务代码加载之前加载
    history.pushState = function (data: any, title: string, url?: string | null | undefined) {
      urlChange();
      return oldPushState.call(history, data, title, url);
    };

    history.replaceState = function (data: any, title: string, url?: string | null | undefined) {
      urlChange();
      return oldReplaceState.call(history, data, title, url);
    };

    window.addEventListener(supportsPushState ? 'popstate' : 'hashchange', urlChange);

    initAutoPV(); // eslint-disable-line

    if (window.EFWebLogger) {
      // @ts-ignore radar日志增加ip_subnet维度
      window.EFWebLogger?.plugins?.radar?.setDimensions({
        c_dimension1: getCookie('ip_subnet')
      });

      //页面关闭时上报webLogger留存日志
      addOnUnloadCallback(function () {
        window.EFWebLogger?.flush();
      });

      //设置默认上报参数
      //https://docs.corp.kuaishou.com/d/home/fcAAv4bDdSVVWvKG3dL1hLZGZ#
      window.EFWebLogger.updateCommonPackage({
        h5_extra_attr: {
          tenancy_id: '0', // docs默认0，eta有租户id
          source_app: getSourceApp(),
          platform: getPlatform(),
          user_type: 'login', //默认login
          uuid,
          user_edition: 'docs',
          docs_id: getDocId() || '',
          //doc-logger版本号
          log_version: process.env.CURRENT_VERSION || 'local'
        }
      });
    }
  }

  return !!window.EFWebLogger;
}

export const loadRadarPromise = loadWebLogger();

export async function setAppAnalyzeUserId(userId: string) {
  await loadRadarPromise;
  window.EFWebLogger?.updateCommonPackage({
    user_id: userId,
    h5_extra_attr: {
      user_id: userId,
      account_id: userId
    }
  });
}

export async function setVersionForAppAnalyze(version: string) {
  await loadRadarPromise;
  window.EFWebLogger?.updateCommonPackage({
    version_name: version
  });
}

type CollectType = EventType;

let setUserInfoEnd: (unTimeout: boolean) => void;
const SetUserInfoPromise = new Promise(resolve => {
  setUserInfoEnd = resolve;
  setTimeout(() => {
    setUserInfoEnd(false);
  }, 10 * 1000);
});

let setWikiIdEnd: (unTimeout: boolean) => void;

//拓展
export function extendExtraAttr(extraAttr: {}) {
  return loadRadarPromise.then(() => {
    window.EFWebLogger?.updateCommonPackage({
      h5_extra_attr: extraAttr
    });
  });
}

extendExtraAttr({
  is_wiki: ~~isWiki
});

const setWikiIdPromise = isWiki
  ? new Promise(resolve => {
      setWikiIdEnd = resolve;
      setTimeout(() => {
        setWikiIdEnd(false);
      }, 10 * 1000);
    })
  : Promise.resolve(true);

export function setWikiId(wikiId: string) {
  setWikiIdEnd(true);
  extendMeta({
    wikiId
  });
  extendExtraAttr({
    wiki_id: wikiId
  });
}

/**
 * 用户业务类型，0=企业版用户，1=个人版用户, 2=商家版用户
 */
const userEditionMap = {
  0: 'eta_enterprise',
  1: 'eta_personal',
  2: 'eta_merchant'
};

/**
 * @param userInfo
 * userBusinessType 仅在eta上传
 */
export function setUserInfo(userInfo: UserInfo) {
  const { identityId, accountId, tenancyId, userType, userBusinessType } = userInfo;

  extendMeta(userInfo);
  const extendAttr: any = {
    ...userInfo, //允许任意扩展
    user_type: userType || 'login'
  };

  if (typeof userBusinessType != 'undefined') {
    extendAttr.user_edition = userEditionMap[userBusinessType] || 'other';
  } else if (isEta) {
    extendAttr.user_edition = 'other';
  }

  //删除预设参数
  delete extendAttr.identityId;
  delete extendAttr.accountId;
  delete extendAttr.tenancyId;
  delete extendAttr.userType;

  if (identityId) {
    extendAttr['identity_id'] = identityId;
  }
  if (accountId) {
    extendAttr['account_id'] = accountId;
  }
  if (tenancyId) {
    extendAttr['tenancy_id'] = tenancyId;
  }
  extendExtraAttr(extendAttr).then(() => {
    setUserInfoEnd(true);
  });
}

const localRecord: {
  type: CollectType;
  options: Object;
}[] = [];

/**
 * 输出本地保存的app分析日志
 * @returns
 */
export function getLocalAppAnalyzeRecord() {
  return localRecord;
}

function triggerRecord(type: CollectType, options: Object) {
  console.info('【app分析上报】', type, options);
  localRecord.push({
    type,
    options
  });
}

export function sendImmediately(type: CollectType, options: Object) {
  triggerRecord(type, options);
  Promise.all([loadRadarPromise, SetUserInfoPromise, setWikiIdPromise]).then(() => {
    window.EFWebLogger?.sendImmediately(type, options);
  });
}

export function collect(type: CollectType, options: Object) {
  triggerRecord(type, options);
  Promise.all([loadRadarPromise, SetUserInfoPromise, setWikiIdPromise]).then(() => {
    window.EFWebLogger?.collect(type, options);
  });
}

export function radarEvent(
  event: {
    name: string;
    src?: string;
    message?: string;
  },
  options?: {
    event_count?: number;
    duration?: number;
  }
) {
  return Promise.all([loadRadarPromise]).then(() => {
    // @ts-ignore radar上报 https://halo.corp.kuaishou.com/help/docs/0cc3893b847f2cbe09c66791a594831d#%E8%87%AA%E5%AE%9A%E4%B9%89%E4%BA%8B%E4%BB%B6%E4%B8%8A%E6%8A%A5
    window?.EFWebLogger?.plugins?.radar?.event(event, options);
  });
}

export function radarCatchJsError(error: Error) {
  return Promise.all([loadRadarPromise]).then(() => {
    // @ts-ignore radar上报 https://halo.corp.kuaishou.com/help/docs/0cc3893b847f2cbe09c66791a594831d#%E8%87%AA%E5%AE%9A%E4%B9%89%E4%BA%8B%E4%BB%B6%E4%B8%8A%E6%8A%A5
    window?.EFWebLogger?.plugins?.radar?.captureException(error);
  });
}

/**
 *原app分析的autoPV在页面Url变更时会导致上报page变化，改为自己实现
 */
function initAutoPV() {
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') {
      collect('PV', {
        type: 'hidden',
        beginTime: beginTime,
        auto: true
      });
    }
    if (document.visibilityState === 'visible') {
      collect('PV', {
        type: 'visible',
        beginTime: beginTime,
        auto: true
      });
    }
  });
  window.addEventListener('beforeunload', function () {
    sendImmediately('PV', {
      type: 'leave',
      beginTime: beginTime,
      auto: true
    });
  });
}
